(() => {
  if (window.sanyoCartAdd) return;

  // 進行中リクエストの共有（多重送信防止）と状態保持
  const pending = new Map();
  const blocked = new Map();
  // 確認待ち状態として保持するメッセージ番号
  const heldCodes = new Set([1, 2, 3, 5, 8, 9, 10, 12, 13, 15]);
  const variantKey = id => String(Number(id));
  // 先読みしたカート情報を、追加前の状態として使える期間
  const prefetchTtlMs = 30000;
  let catalog;
  let prefetched = null;

  // メッセージコードに応じたレスポンスオブジェクトを生成
  function feedback(code, details = {}) {
    return { code, success: false, hold: heldCodes.has(code), ...details };
  }

  // 共通メッセージ文言の取得と置換
  function message(code, limit) {
    if (!catalog) catalog = JSON.parse(document.getElementById('CartAddMessages').textContent);
    if (code === 6 && (!Number.isSafeInteger(limit) || limit < 0)) code = 12;
    const text = catalog[code] || catalog[12];
    return code === 6 ? text.replace('{上限数}', String(limit)) : text;
  }

  // フィードバックメッセージの削除
  function clear(target) {
    target?.querySelector('[data-cart-add-feedback]')?.remove();
    const row = target?.closest?.('.item-list__size');
    if (row && !row.querySelector('[data-cart-add-feedback]')) row.classList.remove('cart-add-error-visible');
  }

  // フィードバックメッセージの表示
  function show(target, result, fallback) {
    let container = target;
    if (!container || container.isConnected === false) container = fallback;
    if (!container || container.isConnected === false) container = document.querySelector('main') || document.body;
    let region = container.querySelector('[data-cart-add-feedback]');
    if (!region) {
      region = document.createElement('div');
      region.className = 'cart-add-feedback';
      region.setAttribute('data-cart-add-feedback', '');
      container.appendChild(region);
    }
    // メッセージ要素の生成とカートリンクの付与
    region.setAttribute('data-message-no', result.code);
    region.setAttribute('role', result.code === 14 || result.code === 13 ? 'status' : 'alert');
    const text = document.createElement('span');
    const content = message(result.code, result.limit);
    const cartLink = content.match(/カート(?:で数量)?をご確認ください/);
    if (cartLink) {
      const link = document.createElement('a');
      link.className = 'cart-add-message-link';
      link.href = window.routes.cart_url;
      link.textContent = cartLink[0];
      text.append(content.slice(0, cartLink.index), link, content.slice(cartLink.index + cartLink[0].length));
    } else {
      text.textContent = content;
    }
    region.replaceChildren(text);
    container.closest?.('.item-list__size')?.classList.add('cart-add-error-visible');
    return region;
  }

  // プロパティの正規化文字列を生成
  function propertiesKey(properties) {
    if (properties == null) return '[]';
    if (typeof properties !== 'object' || Array.isArray(properties)) return null;
    const entries = Object.entries(properties);
    if (entries.some(([, value]) => value != null && typeof value === 'object')) return null;
    return JSON.stringify(entries.map(([key, value]) => [key, String(value ?? '')]).sort(([a], [b]) => a.localeCompare(b)));
  }

  // 明細の識別キーを生成
  function lineIdentity(id, properties, sellingPlan) {
    const propertyKey = propertiesKey(properties);
    return propertyKey === null ? null : JSON.stringify([Number(id), propertyKey, String(sellingPlan || '')]);
  }

  // カート情報の集計
  function snapshot(cart) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
    const lines = new Map();
    const quantities = new Map();
    let total = 0;
    for (const item of cart.items) {
      const id = Number(item?.variant_id ?? item?.id);
      if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(item?.quantity) || item.quantity < 0) {
        throw new Error('Invalid cart line');
      }
      const identity = lineIdentity(id, item.properties, item.selling_plan_allocation?.selling_plan?.id);
      if (identity === null) throw new Error('Invalid cart properties');
      if (!item.quantity) continue;
      total += item.quantity;
      quantities.set(id, (quantities.get(id) || 0) + item.quantity);
      lines.set(identity, (lines.get(identity) || 0) + item.quantity);
    }
    if (cart.item_count !== undefined && cart.item_count !== total) throw new Error('Invalid cart count');
    return { token: typeof cart.token === 'string' ? cart.token : null, lines, quantities, total };
  }

  // カート内容や送信データ全体を含めず、通信の診断に必要な項目だけ記録
  function logCartAddError(phase, request, { endpoint, response, data, error, signal } = {}) {
    try {
      const text = value => typeof value === 'string' ? value.slice(0, 500) : undefined;
      console.error('[cart:add]', {
        phase,
        endpoint,
        variantId: request.variantId,
        quantity: request.quantity,
        httpStatus: response?.status,
        apiStatus: typeof data?.status === 'number' ? data.status : text(data?.status),
        message: text(data?.message),
        description: text(data?.description),
        requestId: response?.headers?.get('x-request-id'),
        // JSON解析エラーの本文にはレスポンスの一部が含まれるため、例外名だけ記録
        errorName: text(error?.name),
        aborted: signal?.aborted === true,
      });
    } catch { /* ログ出力の失敗でカート処理を止めない */ }
  }

  // 最新カート情報の取得
  async function readCart(signal, request, phase) {
    const endpoint = `${window.routes.cart_url}.js`;
    let response;
    try {
      response = await fetch(endpoint, { headers: { Accept: 'application/json' }, signal });
      if (!response.ok) throw new Error('Cart read failed');
      return snapshot(await response.json());
    } catch (error) {
      logCartAddError(phase, request, { endpoint, response, error, signal });
      throw error;
    }
  }

  // カート追加前後の状態比較
  function compare(before, after, request) {
    const identity = lineIdentity(request.variantId, request.properties, request.sellingPlan);
    if (identity === null) return { verified: false, differentCart: false };
    const delta = (after.lines.get(identity) || 0) - (before.lines.get(identity) || 0);
    const otherKeys = new Set([...before.lines.keys(), ...after.lines.keys()]);
    otherKeys.delete(identity);
    const othersMatch = [...otherKeys].every(key => (before.lines.get(key) || 0) === (after.lines.get(key) || 0));
    const expectedChange = othersMatch && delta >= 0 && delta <= request.quantity;
    const sameToken = before.token && before.token === after.token;
    // 初回追加時の判定
    const firstCart = before.total === 0 && delta > 0 && expectedChange;
    const tokenChanged = before.token && after.token && before.token !== after.token;
    const differentCart = !!tokenChanged && !expectedChange;
    return {
      delta, differentCart,
      verified: !!(sameToken || firstCart) && expectedChange && window.sanyoCartSync?.isCurrent?.() !== false,
    };
  }

  // 送信前バリデーション
  function validateInput(request) {
    if (request.selectionMissing) return feedback(4);
    if (request.selectionUnavailable) return feedback(5, { hold: false });
    if (!Number.isSafeInteger(request.variantId) || request.variantId <= 0
      || !Number.isSafeInteger(request.quantity) || request.quantity <= 0 || request.metadataMissing) return feedback(7);
    if (request.available === false || request.handling === 0) return feedback(5);
    if (request.purchaseLimit != null && request.purchaseLimit !== '') {
      const limit = Number(request.purchaseLimit);
      if (!Number.isSafeInteger(limit) || limit < 0) return feedback(7);
      if (request.quantity > limit) return feedback(6, { limit });
    }
    return null;
  }

  // バリエーションIDと数量を数値に変換
  function normalizeRequest(request) {
    return { ...request, variantId: Number(request.variantId), quantity: Number(request.quantity) };
  }

  // カート更新リビジョン（このタブ・別タブでの更新を検知する）
  function cartRevision() {
    const key = window.sanyoCartSync?.revisionStorageKey;
    if (!key) return null;
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  // 追加操作の前（モーダル表示時など）にカート情報を先読みする
  function prefetchCart() {
    // 取得中・追加処理中は先読みしない
    if (prefetched?.loading || pending.size > 0) return;
    const controller = new AbortController();
    // 期限はスリープ中も進む時刻で、取得開始時点から数える
    const entry = { loading: true, controller, at: Date.now(), revision: cartRevision() };
    prefetched = entry;
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    // 先読みの失敗は、通常の追加処理で取得し直すため記録しない
    const discard = () => {
      if (prefetched === entry) prefetched = null;
    };
    try {
      fetch(`${window.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, signal: controller.signal })
        .then(response => {
          if (!response.ok) throw new Error('Cart read failed');
          return response.json();
        })
        .then(cart => {
          if (prefetched === entry) prefetched = { state: snapshot(cart), at: entry.at, revision: entry.revision };
        })
        .catch(discard)
        .finally(() => clearTimeout(timeoutId));
    } catch {
      clearTimeout(timeoutId);
      discard();
    }
  }

  // 先読み結果の破棄（取得中なら通信も中止する）
  function discardPrefetched() {
    prefetched?.controller?.abort();
    prefetched = null;
  }

  // 非表示の間のカート変更（購入完了など）は検知できないため、先読み結果を破棄する
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') discardPrefetched();
  });
  window.addEventListener('pagehide', discardPrefetched);

  // 先読み結果は1回の追加操作だけに使い、期限切れや先読み後の更新があれば使わない
  function takePrefetchedCart() {
    const entry = prefetched;
    discardPrefetched();
    if (!entry?.state || Date.now() - entry.at > prefetchTtlMs || cartRevision() !== entry.revision) return null;
    return entry.state;
  }

  // パラメータ不正エラーの判定
  function inputError(data) {
    return [data?.message, data?.description].some(text => typeof text === 'string'
      && /^(?:Parameter Missing or Invalid[:.]?\s*)?Required parameter missing or invalid/i.test(text));
  }

  // エラー時の状態判定とメッセージ分類
  function classify(status, data, before, after, request) {
    const comparison = after && compare(before, after, request);
    if (comparison?.differentCart) return feedback(15, { mayHaveChanged: true });
    let knownCode;
    if (status === 409) knownCode = 8;
    else if (status === 429) knownCode = 9;
    else if ([402, 403, 423, 430].includes(status)) knownCode = 10;
    const unchanged = comparison?.verified && comparison.delta === 0;
    if (knownCode) return feedback(knownCode, { mayHaveChanged: !unchanged });
    if (!comparison?.verified) return feedback(12, { mayHaveChanged: true });
    if (status === 422) {
      if (comparison.delta > 0 && comparison.delta < request.quantity) return feedback(1, { mayHaveChanged: true });
      const description = typeof data?.description === 'string' ? data.description.trim() : '';
      if (unchanged && /^The maximum quantity of this item is already in your cart\.?$/i.test(description)
        && (after.quantities.get(request.variantId) || 0) > 0) return feedback(2);
      if (unchanged && /^The product .+ is already sold out\.?$/i.test(description)) return feedback(3);
    }
    if (unchanged && inputError(data)) return feedback(7);
    if (status === 400 && comparison.delta === request.quantity) return feedback(13, { mayHaveChanged: true });
    if (unchanged) return feedback(11);
    return feedback(12, { mayHaveChanged: true });
  }

  // カート追加リクエストの実行
  async function execute(request, prefetchedBefore) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    let sent = false;
    try {
      // 送信前のカート状態と上限チェック（先読み済みなら取得を待たずに判定する）
      const before = prefetchedBefore || await readCart(controller.signal, request, 'before-add');
      const limit = request.purchaseLimit == null || request.purchaseLimit === '' ? null : Number(request.purchaseLimit);
      if (limit !== null && (before.quantities.get(request.variantId) || 0) + request.quantity > limit) return feedback(6, { limit });
      if (window.sanyoCartSync?.isCurrent?.() === false) return feedback(8);
      if (controller.signal.aborted) return feedback(11);

      sent = true;
      // カート追加リクエストの送信
      // 同一明細への数量加算と新規明細の追加は cart/add.js で行う（古い数量で上書きする問題を避ける）
      const response = await fetch(`${window.routes.cart_add_url}.js`, { ...request.config, signal: controller.signal });
      // レスポンスの解析
      let data;
      let parseFailed = false;
      try { data = await response.json(); } catch (error) {
        parseFailed = true;
        logCartAddError('response-json', request, {
          endpoint: `${window.routes.cart_add_url}.js`, response, error, signal: controller.signal,
        });
        if (response.ok || response.status >= 500) return feedback(12, { mayHaveChanged: true });
      }
      const status = response.ok ? Number(data?.status) : response.status;
      // エラーレスポンスの記録
      if (!parseFailed && (!response.ok || status >= 400)) {
        logCartAddError('response-error', request, {
          endpoint: `${window.routes.cart_add_url}.js`, response, data, signal: controller.signal,
        });
      }
      // サーバーエラー時は成否不明として扱う
      if (response.status >= 500 || status >= 500) return feedback(12, { mayHaveChanged: true });
      // 成功レスポンスの検証
      if (response.ok && !(status >= 400)) {
        const items = Array.isArray(data?.items) ? data.items : [data];
        if (!items.some(item => item?.id === request.variantId && Number.isSafeInteger(item.quantity) && item.quantity > 0)) {
          logCartAddError('response-items', request, { endpoint: `${window.routes.cart_add_url}.js`, response });
          return feedback(12, { mayHaveChanged: true });
        }
        if (request.sections?.some(id => typeof data.sections?.[id] !== 'string')) {
          logCartAddError('response-sections', request, { endpoint: `${window.routes.cart_add_url}.js`, response });
          return feedback(13, { mayHaveChanged: true });
        }
        return { success: true, data, hold: false };
      }
      // エラー時のカート再取得と状態確認
      let after;
      try { after = await readCart(controller.signal, request, 'after-add'); } catch { /* カート再取得失敗時のフォールバック */ }
      return classify(status, data, before, after, request);
    } catch (error) {
      // 送信前のカート取得失敗は readCart 側で記録済み
      if (sent) {
        logCartAddError('request-failed', request, {
          endpoint: `${window.routes.cart_add_url}.js`, error, signal: controller.signal,
        });
      }
      return feedback(sent ? 12 : 11, { mayHaveChanged: sent });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // 確認待ち状態の保持
  function hold(id, result) {
    if (result.hold) blocked.set(variantKey(id), result);
    return result;
  }

  // カート追加処理のエントリポイント
  async function add(request, onPending) {
    request = normalizeRequest(request);
    const invalid = validateInput(request);
    if (invalid) return hold(request.variantId, invalid);
    const key = variantKey(request.variantId);
    if (blocked.has(key)) return blocked.get(key);
    // 進行中リクエストがある場合は相乗り
    if (pending.has(key)) {
      onPending?.(feedback(14));
      return { ...await pending.get(key), duplicate: true };
    }
    onPending?.(feedback(14));
    // 先読み済みのカート情報があれば追加前の状態として使う（追加後はカートが変わるため、使えない場合も破棄する）
    const operation = execute(request, takePrefetchedCart()).then(result => {
      hold(key, result);
      // 別タブへの更新通知
      try {
        if (result.success) window.sanyoCartSync?.publish('product-add');
        else if (result.mayHaveChanged) window.sanyoCartSync?.publish('product-add-result-unknown');
      } catch { /* 別タブへの通知エラーは無視 */ }
      return result;
    }).finally(() => pending.delete(key));
    pending.set(key, operation);
    return operation;
  }

  // カート追加処理の公開
  window.sanyoCartAdd = {
    add, prefetchCart, show, clear, message, feedback, hold,
    isBlocked: id => blocked.has(variantKey(id)),
    blockedResult: id => blocked.get(variantKey(id)),
    needsReload: () => pending.size > 0 || blocked.size > 0,
  };
})();
